import { motion } from "framer-motion";
import { ClipboardCheck, Mail, CalendarCheck, Wine, Heart } from "lucide-react";

const steps = [
  {
    icon: ClipboardCheck,
    step: "01",
    title: "You register for an event",
    description: "Choose your event, fill out the form, and submit your booking or join the waitlist.",
  },
  {
    icon: Mail,
    step: "02",
    title: "Receive confirmation",
    description: "You'll get a confirmation via email or WhatsApp with your booking details.",
  },
  {
    icon: CalendarCheck,
    step: "03",
    title: "Final details before the event",
    description: "Event specifics — exact venue address, dress code, and timing — are shared 24–48 hours before.",
  },
  {
    icon: Wine,
    step: "04",
    title: "Attend the curated mixer",
    description: "Show up, meet your host, and enjoy structured mini-dates, icebreakers, and conversations.",
  },
  {
    icon: Heart,
    step: "05",
    title: "Mutual matches communicated",
    description: "After the event, submit your preferences. Contacts are shared only where both said yes or maybe.",
  },
];

export default function PostRegistration() {
  return (
    <section
      className="py-24 sm:py-32"
      data-testid="post-registration-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-mono text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(var(--accent))] mb-4">
            Your journey
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-medium max-w-2xl">
            What happens after you register
          </h2>
        </motion.div>

        <div className="relative">
          {/* Vertical line connector (desktop) */}
          <div className="hidden md:block absolute left-6 top-8 bottom-8 w-px bg-border/60" />

          <div className="space-y-6 md:space-y-0 md:grid md:gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex gap-5 md:gap-8 items-start"
                data-testid={`post-reg-step-${i + 1}`}
              >
                <div className="relative z-10 w-12 h-12 rounded-sm bg-card border border-border/40 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <step.icon className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                </div>
                <div className="pb-6 md:pb-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
                      Step {step.step}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl tracking-tight font-medium mb-1">
                    {step.title}
                  </h3>
                  <p className="text-base leading-relaxed text-muted-foreground max-w-md">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
